import type Game from '../scenes/Game'

export interface PlayerPositionForMediaProximity {
  x: number
  y: number
  playerId: string
}

/**
 * Resolve another player's position for LiveKit proximity / subscription.
 * Prefers Phaser sprite; falls back to Colyseus schema or network interpolation map
 * so late joiners can subscribe before otherPlayerMap is populated.
 *
 * SC-257 / TC-03: when the tab is backgrounded the Phaser sprite freezes — its
 * position is driven by requestAnimationFrame, which the browser suspends in a
 * hidden tab. The Colyseus schema keeps updating over the WebSocket regardless,
 * so a colleague walking up while we're in another tab still moves in the schema.
 * We therefore prefer the schema position while hidden (so proximity picks up a
 * newly-approaching participant and subscribes their mic) and keep the sprite
 * first while visible (visual coherence with what's rendered on screen).
 */
export function getPlayerPositionForMediaProximity(
  game: Game | null | undefined,
  sessionId: string
): PlayerPositionForMediaProximity | null {
  if (!game || !sessionId) return null

  const spritePosition = (): PlayerPositionForMediaProximity | null => {
    const sprite = game.getOtherPlayerById(sessionId)
    if (sprite && typeof sprite.x === 'number' && typeof sprite.y === 'number') {
      return { x: sprite.x, y: sprite.y, playerId: sprite.playerId }
    }
    return null
  }

  const schemaPosition = (): PlayerPositionForMediaProximity | null => {
    const room = game.network?.room
    const schemaPlayer = room?.state?.players?.get(sessionId) as
      | { x?: number; y?: number }
      | undefined
    if (
      schemaPlayer &&
      typeof schemaPlayer.x === 'number' &&
      typeof schemaPlayer.y === 'number' &&
      !Number.isNaN(schemaPlayer.x) &&
      !Number.isNaN(schemaPlayer.y)
    ) {
      return { x: schemaPlayer.x, y: schemaPlayer.y, playerId: sessionId }
    }
    return null
  }

  const hidden = typeof document !== 'undefined' && document.hidden
  const ordered = hidden
    ? [schemaPosition, spritePosition]
    : [spritePosition, schemaPosition]
  for (const resolve of ordered) {
    const pos = resolve()
    if (pos) return pos
  }

  const interp = game.network?.players?.get(sessionId) as { x?: number; y?: number } | undefined
  if (
    interp &&
    typeof interp.x === 'number' &&
    typeof interp.y === 'number' &&
    !Number.isNaN(interp.x) &&
    !Number.isNaN(interp.y)
  ) {
    return { x: interp.x, y: interp.y, playerId: sessionId }
  }

  const cached = game.network?.getLastKnownPlayerPosition?.(sessionId)
  if (cached) {
    return { x: cached.x, y: cached.y, playerId: sessionId }
  }

  return null
}
